import { useCallback } from 'react';
import { apiService } from '../services/apiService';
import { storageService } from '../services/storageService';
import { shouldForceLogoutOnAuthError } from '../utils/authErrors';

type TelemetryFn = (event: string, payload?: Record<string, any>, level?: 'info' | 'error', overrideUserId?: string) => void;

type UseSessionLifecycleArgs = {
  server: string;
  sessionResendSeconds: number;
  sessionLockUntil: number | null;
  sessionAttempts: number;
  sessionConsentChecked: boolean;
  logTelemetry: TelemetryFn;
  setIsSessionRequestingCode: (value: boolean) => void;
  setSessionErrorMessage: (value: string | null) => void;
  setSessionStage: (stage: 'email' | 'verify') => void;
  setSessionHintCode: (value: string | null) => void;
  setSessionResendSeconds: (value: number) => void;
  setIsSessionSubmitting: (value: boolean) => void;
  setSession: (value: any) => void;
  setSessionCodeInput: (value: string) => void;
  setSessionAttempts: (value: number) => void;
  setSessionLockUntil: (value: number | null) => void;
  setPendingConsentAcceptance: (value: boolean) => void;
  logout: () => void;
};

export const useSessionLifecycle = ({
  server,
  sessionResendSeconds,
  sessionLockUntil,
  sessionAttempts,
  sessionConsentChecked,
  logTelemetry,
  setIsSessionRequestingCode,
  setSessionErrorMessage,
  setSessionStage,
  setSessionHintCode,
  setSessionResendSeconds,
  setIsSessionSubmitting,
  setSession,
  setSessionCodeInput,
  setSessionAttempts,
  setSessionLockUntil,
  setPendingConsentAcceptance,
  logout
}: UseSessionLifecycleArgs) => {
  const requestSessionCode = useCallback(async (email: string) => {
    if (sessionResendSeconds > 0) return;
    setIsSessionRequestingCode(true);
    setSessionErrorMessage(null);
    try {
      const resp = await apiService.fetchJSON(`${server}/api/session/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      setSessionStage('verify');
      setSessionHintCode(resp?.devCode || null);
      setSessionResendSeconds(30);
      logTelemetry('session_code_request', { email });
    } catch (err: any) {
      console.error('Session code request failed', err);
      setSessionErrorMessage(err.message || 'Could not send verification code. Try again.');
    } finally {
      setIsSessionRequestingCode(false);
    }
  }, [logTelemetry, server, sessionResendSeconds, setIsSessionRequestingCode, setSessionErrorMessage, setSessionHintCode, setSessionResendSeconds, setSessionStage]);

  const verifySessionCode = useCallback(async (email: string, code: string) => {
    if (sessionLockUntil && Date.now() < sessionLockUntil) {
      setSessionErrorMessage('Too many attempts. Please wait and try again.');
      return;
    }
    setIsSessionSubmitting(true);
    setSessionErrorMessage(null);
    try {
      const payload = await apiService.fetchJSON(`${server}/api/session/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      if (payload?.token) {
        const hydrated = { userId: payload.userId, email: payload.email, token: payload.token, workspaceRole: payload.workspaceRole || 'owner' };
        setSession(hydrated);
        storageService.setItem('longevityos:session', JSON.stringify(hydrated));
        logTelemetry('session_start', { email }, 'info', payload?.userId);
        setSessionStage('verify');
        setSessionHintCode(null);
        setSessionCodeInput('');
        setSessionAttempts(0);
        setSessionLockUntil(null);
        if (sessionConsentChecked) {
          setPendingConsentAcceptance(true);
        }
      }
    } catch (err: any) {
      console.error('Session verification failed', err);
      const nextAttempts = sessionAttempts + 1;
      setSessionAttempts(nextAttempts);
      if (nextAttempts >= 5) {
        const lockMs = 2 * 60 * 1000;
        setSessionLockUntil(Date.now() + lockMs);
        setSessionErrorMessage('Too many attempts. Locked for 2 minutes.');
      } else {
        setSessionErrorMessage(err.message || 'Invalid code. Please try again.');
      }
    } finally {
      setIsSessionSubmitting(false);
    }
  }, [logTelemetry, server, sessionAttempts, sessionConsentChecked, sessionLockUntil, setIsSessionSubmitting, setSession, setSessionAttempts, setSessionCodeInput, setSessionErrorMessage, setSessionHintCode, setSessionLockUntil, setSessionStage, setPendingConsentAcceptance]);

  const handleAuthFailure = useCallback((err: any) => {
    if (shouldForceLogoutOnAuthError(err)) {
      logout();
    }
  }, [logout]);

  return { requestSessionCode, verifySessionCode, handleAuthFailure };
};
